/* MolCellQuest 問題データ — 代謝・エネルギー変換
   ★ 配列に追記すればそのまま反映されます（id は全体で一意に）。
   ★ diff : 80〜320。基礎 0-179 / 標準 180-269 / 応用 270+ */

const CAT = { track: "molecule", category: "metabolism", categoryName: "代謝・エネルギー変換" };

export const MOLECULE_METABOLISM = [
  {
    id: "met01", ...CAT,
    title: "解糖系の場所と産物", diff: 100, tags: ["解糖系", "ピルビン酸", "細胞質"],
    explain: `<p>解糖系について、正しい記述はどれか。</p>`,
    type: "radio",
    options: [
      "ミトコンドリアのマトリックスで進行し、グルコース1分子から2分子のピルビン酸を生じる。",
      "細胞質基質で進行し、グルコース1分子から2分子のピルビン酸を生じる。",
      "細胞質基質で進行し、グルコース1分子から1分子のピルビン酸を生じる。",
      "酸素が無いと進行できない。",
    ],
    answer: 1,
    hints: [
      "解糖系は最も古い代謝経路のひとつで、酸素のない環境でも働きます。どこで起こるでしょう。",
      "炭素6個のグルコースが、炭素3個の化合物2つに分かれます。",
    ],
    solution: `<p><b>【ステップ1】場所</b></p>
               <p>解糖系は<b>細胞質基質（サイトゾル）</b>で進みます。
               ミトコンドリアを持たない原核生物にもある、最も普遍的な経路です。</p>
               <p><b>【ステップ2】炭素の勘定</b></p>
               <p>グルコース（C6）→ ピルビン酸（C3）× <b>2分子</b>。
               フルクトース1,6-ビスリン酸がアルドラーゼで2つに割れる段階が分岐点です。</p>
               <p><b>【ステップ3】収支</b></p>
               <table>
                 <tr><th></th><th>消費</th><th>産生</th><th>正味</th></tr>
                 <tr><td>ATP</td><td>2（投資期）</td><td>4（回収期）</td><td><b>+2</b></td></tr>
                 <tr><td>NADH</td><td>—</td><td>2</td><td><b>+2</b></td></tr>
               </table>
               <p><b>【ステップ4】酸素は要らない</b></p>
               <p>解糖系自体に酸素は不要です（D は誤り）。
               ただし NAD⁺ を再生しないと止まってしまうため、</p>
               <ul>
                 <li><b>酸素あり</b>：NADH はミトコンドリアへ渡され、電子伝達系で酸化される</li>
                 <li><b>酸素なし</b>：<b>発酵</b>（乳酸発酵・アルコール発酵）で NAD⁺ を再生する</li>
               </ul>
               <p>激しい運動時に乳酸がたまるのは、この NAD⁺ 再生のためです。
               乳酸は「疲労物質」ではなく、むしろ肝臓で糖新生に再利用されます（コリ回路）。</p>
               <p>したがって、正解は <b>B</b> です。</p>`,
  },

  {
    id: "met02", ...CAT,
    title: "ATPの役割", diff: 105, tags: ["ATP", "高エネルギーリン酸結合", "共役"],
    explain: `<p>ATPが「エネルギーの通貨」と呼ばれる理由として、最も適切なものはどれか。</p>`,
    type: "radio",
    options: [
      "生体内で最も多量に蓄えられているエネルギー貯蔵物質だから。",
      "加水分解のΔGが大きく負で、多くの反応と共役させやすく、合成と分解が速く回るから。",
      "分解しても他の物質に変化しないから。",
      "細胞内で唯一エネルギーを持つ分子だから。",
    ],
    answer: 1,
    hints: [
      "「通貨」は貯め込むものでしょうか、それとも流通するものでしょうか。",
      "ヒトが1日に作るATPは体重に匹敵しますが、体内の総量は数十グラムしかありません。",
    ],
    solution: `<p><b>【ステップ1】ATPは「貯金」ではなく「小銭」</b></p>
               <p>ヒト体内の ATP の総量はわずか <b>50 g程度</b>。
               ところが1日に合成・分解される量は<b>体重に匹敵する数十 kg</b> にもなります。
               つまり ATP は<b>1日に何百回も回転</b>しています。</p>
               <p>長期のエネルギー貯蔵はグリコーゲンや脂肪の役目です。
               ATP は貯めるものではなく、<b>流通させるもの</b>——だから「通貨」なのです。</p>
               <p><b>【ステップ2】なぜ共役しやすいのか</b></p>
               <p>ATP → ADP + Pi の ΔG°' は約 <b>-30.5 kJ/mol</b>。
               これは代謝中間体のなかで<b>中程度</b>の位置にあります。</p>
               <table>
                 <tr><th>化合物</th><th>ΔG°' (kJ/mol)</th></tr>
                 <tr><td>ホスホエノールピルビン酸</td><td>-61.9</td></tr>
                 <tr><td>1,3-ビスホスホグリセリン酸</td><td>-49.3</td></tr>
                 <tr><td><b>ATP</b></td><td><b>-30.5</b></td></tr>
                 <tr><td>グルコース6-リン酸</td><td>-13.8</td></tr>
               </table>
               <p>この中間の位置づけが決定的です。ATP は
               <b>高エネルギー化合物からリン酸基を受け取ることも、
               低エネルギー化合物へ渡すこともできる</b>——
               仲介者として理想的な位置にあるのです。
               もし ATP が最も高エネルギーなら、作ることができません。</p>
               <p><b>【ステップ3】他の選択肢を退ける</b></p>
               <ul>
                 <li>A ⇒ 蓄えは少ないのが特徴です。</li>
                 <li>C ⇒ 分解して ADP と Pi になります。</li>
                 <li>D ⇒ GTP、NADH、FADH₂、アセチルCoA など、他にも多数あります。</li>
               </ul>
               <p>したがって、正解は <b>B</b> です。</p>`,
  },

  {
    id: "met03", ...CAT,
    title: "クエン酸回路の位置づけ", diff: 155, tags: ["TCA回路", "アセチルCoA", "マトリックス"],
    explain: `<p>クエン酸回路（TCA回路）について、<b>誤っている</b>記述はどれか。</p>`,
    type: "radio",
    options: [
      "ミトコンドリアのマトリックスで進行する。",
      "アセチルCoA（C2）がオキサロ酢酸（C4）と結合してクエン酸（C6）になることから始まる。",
      "1回転あたり2分子のCO₂、3分子のNADH、1分子のFADH₂、1分子のGTP（またはATP）を生じる。",
      "回路そのものが直接、大量のATPを合成する。",
    ],
    answer: 3,
    hints: [
      "TCA回路が1回転して直接得られるATP（またはGTP）は何分子でしょうか。",
      "回路の本当の産物は「ATP」ではなく「還元型補酵素」です。",
    ],
    solution: `<p><b>【ステップ1】1回転の収支</b></p>
               <table>
                 <tr><th>産物</th><th>数</th></tr>
                 <tr><td>CO₂</td><td>2</td></tr>
                 <tr><td>NADH</td><td>3</td></tr>
                 <tr><td>FADH₂</td><td>1</td></tr>
                 <tr><td>GTP（ATP）</td><td><b>1だけ</b></td></tr>
               </table>
               <p><b>【ステップ2】回路の本当の役割</b></p>
               <p>直接作る高エネルギーリン酸化合物は<b>わずか1分子</b>です。
               TCA回路の本質は ATP を作ることではなく、
               <b>電子を NADH と FADH₂ に集めて電子伝達系へ渡すこと</b>にあります。</p>
               <p>NADH 1分子は約2.5 ATP、FADH₂ 1分子は約1.5 ATP 相当なので、
               1回転で 3×2.5 + 1×1.5 + 1 = <b>約10 ATP</b> 相当のエネルギーを
               電子伝達系経由で生み出します。<b>直接ではなく間接的</b>なのです。</p>
               <p><b>【ステップ3】もうひとつの顔</b></p>
               <p>TCA回路は分解（異化）専用ではなく、生合成の材料も供給します。</p>
               <ul>
                 <li>α-ケトグルタル酸 → グルタミン酸 → 他のアミノ酸</li>
                 <li>オキサロ酢酸 → アスパラギン酸、糖新生</li>
                 <li>クエン酸 → 細胞質へ出て脂肪酸合成へ</li>
               </ul>
               <p>中間体が抜き取られると回路が回らなくなるため、
               <b>補充反応（アナプレロティック反応）</b>が用意されています。
               ピルビン酸カルボキシラーゼによる
               ピルビン酸 → オキサロ酢酸がその代表です。</p>
               <p>したがって、誤っているのは <b>D</b> です。</p>`,
  },

  {
    id: "met04", ...CAT,
    title: "化学浸透説", diff: 195, tags: ["電子伝達系", "プロトン勾配", "ATP合成酵素"],
    explain: `<p>ミトコンドリアでのATP合成について、化学浸透説（ミッチェル）が説明する内容として
              最も適切なものはどれか。</p>`,
    type: "radio",
    options: [
      "電子伝達系の酵素が直接ADPをリン酸化してATPを作る。",
      "電子伝達に伴ってH⁺が膜間腔へ汲み出され、その電気化学的勾配を利用してATP合成酵素がATPを作る。",
      "NADHが直接ATP合成酵素に結合してATPを作る。",
      "内膜を横切るNa⁺勾配がATP合成の駆動力になる。",
    ],
    answer: 1,
    hints: [
      "電子伝達系とATP合成酵素は、物理的に別のタンパク質複合体です。両者は何でつながっているでしょう。",
      "ミトコンドリアの内膜に穴を開ける薬剤（脱共役剤）を加えると、ATP合成だけが止まります。",
    ],
    solution: `<p><b>【ステップ1】化学浸透説の核心</b></p>
               <p>電子伝達系と ATP 合成は<b>直接つながっていません</b>。
               両者を結ぶのは <b>H⁺ の電気化学的勾配（プロトン駆動力）</b>という
               「中間通貨」です。</p>
               <ol>
                 <li>NADH・FADH₂ から電子が複合体 I〜IV へ流れる</li>
                 <li>そのエネルギーで H⁺ がマトリックス → <b>膜間腔</b>へ汲み出される</li>
                 <li>膜間腔が酸性・正電位になる（プロトン駆動力の蓄積）</li>
                 <li>H⁺ が <b>ATP合成酵素（複合体V）</b>を通って戻る</li>
                 <li>その流れが c リングを回転させ、F₁部分の構造変化で ATP が合成される</li>
               </ol>
               <p><b>【ステップ2】なぜ当初は受け入れられなかったか</b></p>
               <p>1961年の提唱当時、生化学者は「高エネルギー中間体」が
               あるはずだと探していました。「勾配がエネルギーを運ぶ」という発想は異質で、
               受け入れられるまで十数年かかりました。</p>
               <p><b>【ステップ3】決め手となった証拠</b></p>
               <ul>
                 <li><b>脱共役剤</b>（DNP など）が内膜に H⁺ を通す穴を作ると、
                     電子伝達は続くのに ATP 合成だけが止まる → 両者は勾配を介してつながっている</li>
                 <li>人工的に膜の内外に pH 差を作るだけで、
                     電子伝達なしに ATP が合成できる</li>
                 <li>ATP合成酵素が実際に<b>回転する</b>ことが、
                     蛍光標識アクチン繊維を使って直接可視化された（吉田・木下ら）</li>
               </ul>
               <p class="tiny">褐色脂肪組織の UCP1（サーモゲニン）は、
               この脱共役をわざと起こして<b>熱を産生</b>します。
               冬眠動物や新生児の体温維持に使われる仕組みです。</p>
               <p>したがって、正解は <b>B</b> です。</p>`,
  },

  {
    id: "met05", ...CAT,
    title: "グルコース1分子から得られるATP", diff: 175, tags: ["計算", "ATP収支", "好気呼吸"],
    explain: `<p>グルコース1分子を好気的に完全酸化したとき、
              現在の見積もりで得られるATPはおよそ何分子か。</p>
              <p>NADH = 2.5 ATP、FADH₂ = 1.5 ATP として計算せよ。</p>
              <table>
                <tr><th>段階</th><th>ATP</th><th>NADH</th><th>FADH₂</th></tr>
                <tr><td>解糖系</td><td>2</td><td>2</td><td>0</td></tr>
                <tr><td>ピルビン酸→アセチルCoA（×2）</td><td>0</td><td>2</td><td>0</td></tr>
                <tr><td>クエン酸回路（×2回転）</td><td>2</td><td>6</td><td>2</td></tr>
              </table>`,
    type: "radio",
    options: ["約 30 ATP", "約 32 ATP", "約 36 ATP", "約 38 ATP"],
    answer: 1,
    hints: [
      "NADH は合計10分子、FADH₂ は合計2分子です。",
      "4（直接のATP）+ 10×2.5 + 2×1.5 を計算しましょう。",
    ],
    solution: `<p><b>【ステップ1】還元型補酵素を合計する</b></p>
               <ul>
                 <li>NADH：2（解糖系）+ 2（ピルビン酸脱水素酵素）+ 6（TCA）= <b>10</b></li>
                 <li>FADH₂：<b>2</b>（TCA）</li>
                 <li>直接の ATP/GTP：2（解糖系）+ 2（TCA）= <b>4</b></li>
               </ul>
               <p><b>【ステップ2】ATP に換算する</b></p>
               <table>
                 <tr><th>由来</th><th>計算</th><th>ATP</th></tr>
                 <tr><td>直接</td><td>—</td><td>4</td></tr>
                 <tr><td>NADH</td><td>10 × 2.5</td><td>25</td></tr>
                 <tr><td>FADH₂</td><td>2 × 1.5</td><td>3</td></tr>
                 <tr><td colspan="2"><b>合計</b></td><td><b>32</b></td></tr>
               </table>
               <p><b>【ステップ3】なぜ「約38」ではないのか</b></p>
               <p>古い教科書では NADH = 3、FADH₂ = 2 として 38 と教えていました。
               現在 2.5 / 1.5 という半端な値が使われるのは、次の理由からです。</p>
               <ul>
                 <li>ATP合成酵素の c リングの数から、ATP 1分子に約 <b>4 個の H⁺</b> が必要と分かった
                     （うち1個は ATP/ADP・Pi の輸送に使われる）</li>
                 <li>NADH 1分子で汲み出される H⁺ は10個 → 10/4 = <b>2.5</b></li>
                 <li>FADH₂ は複合体 I を迂回するため6個 → 6/4 = <b>1.5</b></li>
               </ul>
               <p>整数比にならないのは、<b>H⁺ の輸送と ATP 合成が
               整数で対応していない</b>からです。「化学量論的な結合ではなく、
               勾配という連続量を介している」という化学浸透説の帰結が、
               この半端な数字に表れています。</p>
               <p class="tiny">実際の収量は、細胞質の NADH をどのシャトルで運ぶか
               （リンゴ酸-アスパラギン酸シャトルなら2.5、グリセロールリン酸シャトルなら1.5）でも変わり、
               30〜32 の幅で語られます。</p>
               <p>したがって、正解は <b>約 32 ATP (B)</b> です。</p>`,
  },

  {
    id: "met06", ...CAT,
    title: "β酸化と脂肪酸のエネルギー", diff: 240, tags: ["β酸化", "脂肪酸", "エネルギー密度"],
    explain: `<p>脂肪が糖よりも効率のよいエネルギー貯蔵物質である理由として、
              最も適切なものはどれか。</p>`,
    type: "radio",
    options: [
      "脂肪酸は炭素あたりの還元度が高く、かつ疎水性で水和を必要としないため、単位重量あたりのエネルギーが大きい。",
      "脂肪酸は解糖系で直接分解できるため、素早くエネルギーになる。",
      "脂肪酸は分解時に酸素を必要としないため。",
      "脂肪酸はグリコーゲンより分子量が小さいため。",
    ],
    answer: 0,
    hints: [
      "グリコーゲンは水と一緒に貯蔵されます。脂肪はどうでしょうか。",
      "脂肪酸の炭素はほとんど -CH₂- で、酸素をほとんど含みません。これは「まだ酸化されていない」ことを意味します。",
    ],
    solution: `<p><b>【ステップ1】2つの理由を分けて考える</b></p>
               <p><b>理由1：還元度が高い</b><br>
               脂肪酸の炭素鎖はほぼ -CH₂- の繰り返しで、酸素をほとんど含みません。
               対して糖は (CH₂O)ₙ で、すでに部分的に酸化されています。
               <b>酸化されていない＝まだ取り出せるエネルギーが多い</b>ということです。</p>
               <table>
                 <tr><th></th><th>エネルギー密度</th></tr>
                 <tr><td>脂肪</td><td>約 <b>9 kcal/g</b></td></tr>
                 <tr><td>糖・タンパク質</td><td>約 4 kcal/g</td></tr>
               </table>
               <p><b>理由2：水を伴わない</b><br>
               グリコーゲンは親水性で、その<b>約2倍の重量の水</b>を伴って貯蔵されます。
               実効的なエネルギー密度は 4 ÷ 3 ≒ 1.3 kcal/g まで下がります。
               脂肪は疎水性なので、ほぼ無水で貯蔵できます。</p>
               <p><b>【ステップ2】どれくらい違うのか</b></p>
               <p>体重70 kgの人が蓄える脂肪（約12 kg）と同じエネルギーを
               グリコーゲンで持とうとすると、水を含めて<b>80 kg以上</b>必要になります。
               陸上を動く動物にとって、これは決定的な差です。</p>
               <p><b>【ステップ3】ではなぜ糖も使うのか</b></p>
               <p>脂肪は万能ではありません。</p>
               <ul>
                 <li>β酸化はミトコンドリアを必要とし、<b>嫌気的には使えない</b>
                     （赤血球や無酸素運動時の筋肉は糖に頼る）</li>
                 <li>動員が遅い</li>
                 <li>脂肪酸からは糖新生ができない（奇数鎖の一部を除く）ため、
                     脳が必要とするグルコースを作れない</li>
               </ul>
               <p>「脂肪＝長期の貯蔵庫、糖＝即座に使える運転資金」という
               役割分担になっているのです。</p>
               <p class="tiny">B が誤りである点も重要です。脂肪酸は解糖系ではなく
               <b>β酸化</b>でアセチルCoAに分解され、TCA回路に入ります。
               C16のパルミチン酸1分子からは約106 ATP が得られます。</p>
               <p>したがって、正解は <b>A</b> です。</p>`,
  },

  {
    id: "met07", ...CAT,
    title: "代謝経路の律速段階", diff: 215, tags: ["ホスホフルクトキナーゼ", "フィードバック阻害", "調節"],
    explain: `<p>解糖系の主要な調節点はホスホフルクトキナーゼ-1（PFK-1）である。</p>
              <p>PFK-1 が <b>ATP によって阻害される</b>ことの意義として、最も適切なものはどれか。</p>`,
    type: "radio",
    options: [
      "ATPが十分にあるとき、それ以上グルコースを分解しないようにするため。",
      "ATPが不足しているとき、解糖系を加速するため。",
      "ATPを基質として消費し尽くすため。",
      "グルコースを細胞外へ排出するため。",
    ],
    answer: 0,
    hints: [
      "解糖系の目的は何でしょうか。その目的がすでに達成されていたら、どうすべきでしょう。",
      "これは典型的なフィードバック阻害です。最終産物が経路の入口を止めます。",
    ],
    solution: `<p><b>【ステップ1】フィードバック阻害の論理</b></p>
               <p>解糖系の主目的は ATP の産生です。
               その ATP が十分にあるなら、経路を動かし続けるのは
               <b>基質と資源の無駄</b>です。だから最終産物 ATP が
               経路の律速酵素を止めます。</p>
               <p>逆に ATP が減って <b>AMP・ADP</b> が増えると、
               これらが PFK-1 を活性化して解糖系を加速します。
               ATP が阻害剤、AMP が活性化剤という<b>逆向きのペア</b>で
               エネルギー状態を鋭敏に感知しているのです。</p>
               <p><b>【ステップ2】なぜ PFK-1 が調節点なのか</b></p>
               <p>解糖系にはATPを消費する不可逆反応が3つあります
               （ヘキソキナーゼ、PFK-1、ピルビン酸キナーゼ）。
               なかでも PFK-1 が<b>解糖系に固有の最初の不可逆段階</b>です。</p>
               <p>グルコース6-リン酸はまだペントースリン酸経路やグリコーゲン合成にも行けますが、
               フルクトース1,6-ビスリン酸になると<b>もう解糖系に進むしかありません</b>。
               「後戻りできない分岐点を制御する」——これは代謝制御の一般原則です。</p>
               <p><b>【ステップ3】多重の調節</b></p>
               <table>
                 <tr><th>因子</th><th>作用</th><th>意味</th></tr>
                 <tr><td>ATP</td><td>阻害</td><td>エネルギー充足</td></tr>
                 <tr><td>AMP / ADP</td><td>活性化</td><td>エネルギー不足</td></tr>
                 <tr><td>クエン酸</td><td>阻害</td><td>TCA回路が飽和している</td></tr>
                 <tr><td>H⁺（低pH）</td><td>阻害</td><td>乳酸蓄積の防止</td></tr>
                 <tr><td><b>フルクトース2,6-ビスリン酸</b></td><td><b>強力に活性化</b></td><td>ホルモンによる全身調節</td></tr>
               </table>
               <p>特にフルクトース2,6-ビスリン酸は、インスリン／グルカゴンのシグナルを受けて
               増減し、<b>個体レベルの栄養状態</b>を細胞の解糖系に伝えます。
               細胞内の需給（ATP/AMP）と全身の状況（ホルモン）の
               両方が1つの酵素に集約されているわけです。</p>
               <p>したがって、正解は <b>A</b> です。</p>`,
  },

  {
    id: "met08", ...CAT,
    title: "光合成と呼吸の対応関係", diff: 185, tags: ["光合成", "カルビン回路", "比較"],
    explain: `<p>光合成の明反応とミトコンドリアの電子伝達系には共通点が多い。</p>
              <p>両者の対応として<b>適切でない</b>ものはどれか。</p>`,
    type: "radio",
    options: [
      "どちらも膜を横切るH⁺勾配を作り、ATP合成酵素で ATP を作る。",
      "どちらも電子伝達に伴って H⁺ が輸送される。",
      "電子の最終的な受け手は、呼吸ではO₂、光合成ではNADP⁺である。",
      "どちらも電子の供給源は NADH である。",
    ],
    answer: 3,
    hints: [
      "光合成で電子はどこから来るでしょうか。水が分解されて何が発生しますか。",
      "呼吸は電子を「受け取って水を作る」、光合成は「水を分解して電子を取り出す」——向きが逆です。",
    ],
    solution: `<p><b>【ステップ1】両者を並べる</b></p>
               <table>
                 <tr><th></th><th>呼吸（ミトコンドリア）</th><th>光合成（葉緑体）</th></tr>
                 <tr><td>電子の供給源</td><td><b>NADH・FADH₂</b></td><td><b>水（H₂O）</b></td></tr>
                 <tr><td>電子の最終受容体</td><td><b>O₂</b>（→ 水）</td><td><b>NADP⁺</b>（→ NADPH）</td></tr>
                 <tr><td>H⁺が溜まる側</td><td>膜間腔</td><td>チラコイド内腔</td></tr>
                 <tr><td>エネルギー源</td><td>基質の酸化</td><td>光</td></tr>
                 <tr><td>ATP合成</td><td colspan="2" style="text-align:center">どちらも H⁺ 勾配 + ATP合成酵素</td></tr>
               </table>
               <p><b>【ステップ2】D が誤りである理由</b></p>
               <p>光合成の電子は <b>水の光分解</b>から来ます。<br>
               2H₂O → 4H⁺ + 4e⁻ + O₂<br>
               このとき発生する O₂ が、私たちが吸っている酸素です。
               NADH ではありません。</p>
               <p><b>【ステップ3】向きが逆であることの意味</b></p>
               <p>2つの過程はエネルギー的に正反対です。</p>
               <ul>
                 <li><b>呼吸</b>：電子を高エネルギー（NADH）から低エネルギー（O₂）へ落とし、
                     その落差でエネルギーを取り出す（自発的、ΔG < 0）</li>
                 <li><b>光合成</b>：電子を低エネルギー（水）から高エネルギー（NADPH）へ
                     <b>光のエネルギーで押し上げる</b>（非自発的、ΔG > 0）</li>
               </ul>
               <p>光化学系IIとIで2段階に分けて電子を持ち上げる仕組みは、
               エネルギー準位図の形から <b>Zスキーム</b>と呼ばれます。
               1段階では水から NADP⁺ まで持ち上げるエネルギーが足りないため、
               2回の光吸収が必要なのです。</p>
               <p class="tiny">なおカルビン回路（暗反応）は光を直接使わず、
               明反応が作った ATP と NADPH を使って CO₂ を固定します。
               「暗所でも進む」意味であって「夜に進む」わけではありません。</p>
               <p>したがって、適切でないのは <b>D</b> です。</p>`,
  },

  {
    id: "met09", ...CAT,
    title: "阻害剤の作用点を実験から特定する", diff: 280, tags: ["電子伝達系", "阻害剤", "実験推論"],
    explain: `<p>単離ミトコンドリアに基質と ADP を加え、酸素消費速度を測った。</p>
              <table>
                <tr><th>条件</th><th>酸素消費</th></tr>
                <tr><td>コハク酸 + ADP</td><td>速い</td></tr>
                <tr><td>コハク酸 + ADP + 薬剤 Z</td><td><b>停止</b></td></tr>
                <tr><td>コハク酸 + ADP + 薬剤 Z + 脱共役剤</td><td><b>停止したまま</b></td></tr>
              </table>
              <p>薬剤 Z の作用点として最も考えられるものはどれか。</p>`,
    type: "radio",
    options: [
      "ATP合成酵素（複合体V）の阻害",
      "電子伝達系そのもの（複合体 III または IV）の阻害",
      "ADP/ATP トランスロカーゼの阻害",
      "複合体 I の阻害",
    ],
    answer: 1,
    hints: [
      "脱共役剤は H⁺ を膜に通し、勾配を消します。ATP合成に依存せず電子伝達だけを回せる状態にします。",
      "脱共役剤を加えても回復しないなら、止まっているのは勾配より「上流」です。",
    ],
    solution: `<p><b>【ステップ1】脱共役剤を使った切り分けの原理</b></p>
               <p>脱共役剤（DNP、FCCP）は内膜に H⁺ を通し、勾配を消してしまいます。
               すると ATP は作れなくなりますが、
               <b>電子伝達系は抑制がとれて最大速度で回ります</b>（酸素消費は増える）。</p>
               <p>この性質を使うと、阻害点が<b>勾配より上流か下流か</b>を判別できます。</p>
               <table>
                 <tr><th>阻害点</th><th>脱共役剤を加えると</th></tr>
                 <tr><td>ATP合成酵素、ADP/ATP輸送（<b>下流</b>）</td><td><b>酸素消費が回復する</b></td></tr>
                 <tr><td>電子伝達系そのもの（<b>上流</b>）</td><td><b>回復しない</b></td></tr>
               </table>
               <p><b>【ステップ2】データを当てはめる</b></p>
               <p>脱共役剤を加えても<b>停止したまま</b>なので、
               薬剤 Z は<b>電子伝達系そのもの</b>を止めています。</p>
               <p><b>【ステップ3】複合体 I を除外する</b></p>
               <p>基質が<b>コハク酸</b>である点が決め手です。
               コハク酸はコハク酸脱水素酵素（<b>複合体 II</b>）で酸化され、
               電子は複合体 I を通らずに直接ユビキノンへ入ります。</p>
               <p>したがって複合体 I が阻害されていても、
               コハク酸を基質にすれば電子伝達は進むはずです。
               それでも止まっているのだから、阻害点は
               <b>複合体 II より下流、つまり複合体 III か IV</b>です。</p>
               <p><b>【ステップ4】既知の阻害剤に当てはめる</b></p>
               <table>
                 <tr><th>阻害剤</th><th>作用点</th></tr>
                 <tr><td>ロテノン</td><td>複合体 I</td></tr>
                 <tr><td>アンチマイシンA</td><td>複合体 III</td></tr>
                 <tr><td>シアン化物、CO、アジ化物</td><td>複合体 IV</td></tr>
                 <tr><td>オリゴマイシン</td><td>ATP合成酵素</td></tr>
                 <tr><td>アトラクチロシド</td><td>ADP/ATP トランスロカーゼ</td></tr>
               </table>
               <p>薬剤 Z はアンチマイシンA やシアン化物のような挙動を示しています。
               <b>もし NADH 産生基質（リンゴ酸+ピルビン酸）で止まり、
               コハク酸では止まらなければ複合体 I</b>と判定できます。
               基質を変えることが、そのまま切り分けの実験になるわけです。</p>
               <p>したがって、正解は <b>B</b> です。</p>`,
  },

  {
    id: "met10", ...CAT,
    title: "ワールブルク効果", diff: 300, tags: ["ワールブルク効果", "がん代謝", "好気的解糖"],
    explain: `<p>多くのがん細胞は、酸素が十分にある条件下でも
              解糖系を活発に使い、乳酸を大量に産生する（ワールブルク効果）。</p>
              <p>ATP収率だけを見れば非効率なこの代謝が、
              増殖するがん細胞にとって有利である理由として最も適切なものはどれか。</p>`,
    type: "radio",
    options: [
      "解糖系のほうがグルコース1分子あたりのATP収率が高いため。",
      "増殖には ATP だけでなく核酸・脂質・アミノ酸の材料が大量に必要で、解糖系の中間体がその供給源になるため。",
      "がん細胞にはミトコンドリアが存在しないため。",
      "乳酸そのものが細胞増殖のエネルギー源になるため。",
    ],
    answer: 1,
    hints: [
      "細胞が「2倍になる」ために必要なのは、エネルギーだけでしょうか。",
      "分裂するには、DNA・膜・タンパク質を丸ごと1セット新造しなければなりません。",
    ],
    solution: `<p><b>【ステップ1】一見すると不合理</b></p>
               <table>
                 <tr><th></th><th>ATP/グルコース</th><th>速度</th></tr>
                 <tr><td>好気的解糖（ワールブルク）</td><td>2</td><td>非常に速い</td></tr>
                 <tr><td>酸化的リン酸化</td><td>約32</td><td>遅い</td></tr>
               </table>
               <p>効率は16分の1。それでもがん細胞がこちらを選ぶのはなぜか。</p>
               <p><b>【ステップ2】発想の転換 — 制限因子は ATP ではない</b></p>
               <p>増殖する細胞にとっての制約は、エネルギーではなく
               <b>体を作る材料（バイオマス）</b>です。
               分裂して2個になるには、DNA・RNA・膜脂質・タンパク質を
               まるごと1セット新造しなければなりません。</p>
               <p>ここで重要なのは、酸化的リン酸化が炭素を
               <b>CO₂ として完全に燃やし尽くしてしまう</b>ことです。
               燃やした炭素は材料になりません。</p>
               <p><b>【ステップ3】解糖系の中間体が材料になる</b></p>
               <table>
                 <tr><th>中間体</th><th>行き先</th><th>作られるもの</th></tr>
                 <tr><td>グルコース6-リン酸</td><td>ペントースリン酸経路</td><td>リボース（核酸）、<b>NADPH</b></td></tr>
                 <tr><td>3-ホスホグリセリン酸</td><td>セリン合成経路</td><td>セリン、グリシン、一炭素単位</td></tr>
                 <tr><td>ジヒドロキシアセトンリン酸</td><td>グリセロール-3-リン酸</td><td>膜リン脂質</td></tr>
                 <tr><td>ピルビン酸 → クエン酸</td><td>細胞質へ搬出</td><td>脂肪酸、コレステロール</td></tr>
               </table>
               <p>とくに <b>NADPH</b> は、脂肪酸合成と
               活性酸素種の消去の両方に不可欠です。</p>
               <p><b>【ステップ4】速度という利点</b></p>
               <p>解糖系は酸化的リン酸化より<b>10〜100倍速く</b> ATP を作れます。
               グルコースを潤沢に取り込める環境なら、
               「効率は悪いが速い」経路のほうが単位時間あたりの ATP 産生量では勝ります。</p>
               <p><b>【ステップ5】臨床応用と誤解の訂正</b></p>
               <p>この性質は診断に直結します。<b>FDG-PET</b> は
               グルコース類似体の取り込みを画像化するもので、
               ワールブルク効果を示す腫瘍が光って見えます。</p>
               <p>なお C は明確な誤りです。がん細胞にもミトコンドリアはあり、
               多くは機能しています。ワールブルク自身は
               「ミトコンドリアの損傷がある」と考えましたが、
               現在では<b>損傷ではなく積極的な選択</b>だと理解されています。
               実際、活発に増殖する正常細胞（活性化リンパ球、胚性幹細胞）も
               同じ代謝様式を示します。</p>
               <p>したがって、正解は <b>B</b> です。</p>`,
  },
];
